import { Reducer } from 'redux';
import { emptyCustomer } from 'types/Customer';
import {
    ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE,
    ONLINE_BOOKING_AGREEMENT_SIGNATURE_PERSISTED,
    ONLINE_BOOKING_AGREEMENT_SIGNATURE_UPDATED,
    ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_START,
    ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_SUCCESS,
    ONLINE_BOOKING_CLEAR_BOARDING_AVAILABILITY,
    ONLINE_BOOKING_CLEAR_DAYCARE_AVAILABILITY,
    ONLINE_BOOKING_CLEAR_WALKING_ELIGIBILITY,
    ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_START,
    ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_SUCCESS,
    ONLINE_BOOKING_CREATE_BOOKING_START,
    ONLINE_BOOKING_CREATE_BOOKING_SUCCESS,
    ONLINE_BOOKING_CREATE_CUSTOMER_START,
    ONLINE_BOOKING_CREATE_CUSTOMER_SUCCESS,
    ONLINE_BOOKING_CREATE_WAITLIST_START,
    ONLINE_BOOKING_CREATE_WAITLIST_SUCCESS,
    ONLINE_BOOKING_ERROR,
    ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_FAILED,
    ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_START,
    ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_SUCCESS,
    ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_FAILED,
    ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_START,
    ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_SUCCESS,
    ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_START,
    ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_SUCCESS,
    ONLINE_BOOKING_GET_GROOMING_STAFF_NEXT_AVAILABLE_DATE_START,
    ONLINE_BOOKING_GET_GROOMING_STAFF_NEXT_AVAILABLE_DATE_SUCCESS,
    ONLINE_BOOKING_GET_WALKING_AVAILABILITY_FAILED,
    ONLINE_BOOKING_GET_WALKING_AVAILABILITY_START,
    ONLINE_BOOKING_GET_WALKING_AVAILABILITY_SUCCESS,
    ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_FAILED,
    ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_START,
    ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_SUCCESS,
    ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_FAILED,
    ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_START,
    ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_SUCCESS,
    ONLINE_BOOKING_INIT,
    ONLINE_BOOKING_INIT_SUCCESS,
    ONLINE_BOOKING_NAVIGATE_TO_STEP,
    ONLINE_BOOKING_RECOVER_WALKING_BOOKING_ERROR,
    ONLINE_BOOKING_REMOVE_BOOKING_ITEMS,
    ONLINE_BOOKING_RESET,
    ONLINE_BOOKING_SAVE_PET_START,
    ONLINE_BOOKING_SAVE_PET_SUCCESS,
    ONLINE_BOOKING_SET_BOOKING_ITEMS,
    ONLINE_BOOKING_SET_CHECKOUT_VIEW,
    ONLINE_BOOKING_SET_CUSTOMER,
    ONLINE_BOOKING_SET_EVALUATION_SERVICE_CATEGORY,
    ONLINE_BOOKING_SET_NOTES,
    ONLINE_BOOKING_SET_SELECTED_CATEGORY,
    ONLINE_BOOKING_SET_SELECTED_PETS,
    ONLINE_BOOKING_SET_SELECTED_STAFF,
    ONLINE_BOOKING_SET_WAITLIST_CATEGORY,
    ONLINE_BOOKING_START_OVER,
    ONLINE_BOOKING_UPDATE_BOOKING_ITEM,
    ONLINE_BOOKING_UPDATE_BOOKING_ITEMS,
    OnlineBookingAction
} from 'actions/onlineBooking/OnlineBookingActionsTypes';
import {
    OnlineBookingState,
    OnlineBookingStatus,
    OnlineBookingWalkingCache
} from 'reducers/onlineBooking/OnlineBookingState';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import {
    countCompletedOnlineBookingCategories,
    isOnlineBookingSessionItemOf,
    OnlineBookingItem,
    OnlineBookingStep,
    SESSION_BUSINESS_AREAS,
    SessionBusinessArea
} from 'types/OnlineBooking';
import {
    clearWalkingItemDateTime,
    clearWalkingItemStaffSelection,
    clearWalkingItemTime,
    walkingUpdateClearsBookingError
} from 'types/Walking';

const ONLINE_BOOKING_CHANNEL = 'online-booking';

// Steps that belong to the waitlist flow. Navigating anywhere else drops the waitlist
// context, so e.g. the boarding add-ons step goes back to its regular behavior.
const WAITLIST_STEPS = new Set<OnlineBookingStep>([
    OnlineBookingStep.AddonList,
    OnlineBookingStep.JoinWaitlist,
    OnlineBookingStep.WaitlistSuccess
]);

const emptySessionCache = (): OnlineBookingWalkingCache => ({
    eligibility: {},
    staffAvailability: {},
    availability: {}
});

const updateSessionCache = (
    state: OnlineBookingState,
    businessArea: SessionBusinessArea,
    update: (cache: OnlineBookingWalkingCache) => OnlineBookingWalkingCache
): OnlineBookingState => ({
    ...state,
    [businessArea]: update(state[businessArea])
});

const startRequest = <T>(
    entries: Record<string, { loading: boolean; data?: T; error?: string }>,
    requestKey: string
) => ({
    ...entries,
    [requestKey]: { loading: true, data: entries[requestKey]?.data, error: undefined }
});

const failRequest = <T>(
    entries: Record<string, { loading: boolean; data?: T; error?: string }>,
    requestKey: string,
    error?: string
) => ({
    ...entries,
    [requestKey]: { loading: false, data: entries[requestKey]?.data, error }
});

const initialState: OnlineBookingState = {
    isLoading: false,
    isPending: false,

    status: OnlineBookingStatus.Initial,

    currentStep: OnlineBookingStep.Home,
    checkoutView: 'cards_list',

    customer: emptyCustomer(ONLINE_BOOKING_CHANNEL),

    selectedPets: [],

    bookingItems: [],

    daycareAvailabilityByDate: {},
    daycareAvailabilityLoadingByDate: {},
    daycareAvailabilityRequestKeyByDate: {},

    completedCategories: 0,

    daycareAvailabilityLoading: false,

    boardingAvailabilityLoading: false,

    [MarketplaceServiceCategory.WALKING]: emptySessionCache(),
    [MarketplaceServiceCategory.TRAINING]: emptySessionCache(),

    agreementSignatures: []
};

export const onlineBookingReducer: Reducer<OnlineBookingState, OnlineBookingAction> = (
    state = initialState,
    action
) => {
    switch (action.type) {
        case ONLINE_BOOKING_RESET:
            return initialState;
        case ONLINE_BOOKING_START_OVER:
            return {
                ...initialState,
                status: OnlineBookingStatus.Initialized,
                currentStep: state.currentStep,
                customer: state.customer
            };
        case ONLINE_BOOKING_ERROR:
            return {
                ...state,
                ...(action.payload ?? {}),
                isLoading: false,
                isPending: false,
                daycareAvailabilityLoadingByDate: {},
                boardingAvailabilityLoading: false,
                status: OnlineBookingStatus.Error
            };
        case ONLINE_BOOKING_INIT:
            return {
                ...state,
                isLoading: true,
                status: OnlineBookingStatus.Initializing
            };
        case ONLINE_BOOKING_INIT_SUCCESS:
            return {
                ...state,
                isLoading: false,
                status: OnlineBookingStatus.Initialized
            };
        case ONLINE_BOOKING_NAVIGATE_TO_STEP:
            return {
                ...state,
                currentStep: action.payload.step,
                waitlistCategory: WAITLIST_STEPS.has(action.payload.step)
                    ? state.waitlistCategory
                    : undefined,
                completedCategories:
                    action.payload.step === OnlineBookingStep.ServiceCategory
                        ? countCompletedOnlineBookingCategories(state.bookingItems)
                        : state.completedCategories,
                checkoutView:
                    action.payload.step === OnlineBookingStep.Checkout
                        ? 'cards_list'
                        : state.checkoutView
            };
        case ONLINE_BOOKING_SET_CHECKOUT_VIEW:
            return {
                ...state,
                checkoutView: action.payload.view
            };
        /*
         * Customer
         */
        case ONLINE_BOOKING_CREATE_CUSTOMER_START:
            return {
                ...state,
                isPending: true,
                status: OnlineBookingStatus.CreateCustomerStart
            };
        case ONLINE_BOOKING_CREATE_CUSTOMER_SUCCESS:
            return {
                ...state,
                isPending: false,
                status: OnlineBookingStatus.CreateCustomerSuccess,
                customer: action.payload.customer
            };
        case ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_START:
            return {
                ...state,
                isPending: true,
                status: OnlineBookingStatus.AuthenticateCustomerStart
            };
        case ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_SUCCESS:
            return {
                ...state,
                isPending: false,
                status: OnlineBookingStatus.AuthenticateCustomerSuccess,
                customer: action.payload.customer
            };
        case ONLINE_BOOKING_SET_CUSTOMER:
            return {
                ...state,
                customer: action.payload.customer
            };
        /*
         * Pets
         */
        case ONLINE_BOOKING_SAVE_PET_START:
            return {
                ...state,
                isPending: true,
                status: OnlineBookingStatus.SavePetStart
            };
        case ONLINE_BOOKING_SAVE_PET_SUCCESS: {
            const existingPetIndex = state.customer.pets.findIndex(
                pet => pet.uuid === action.payload.pet.uuid
            );

            return {
                ...state,
                isPending: false,
                status: OnlineBookingStatus.SavePetSuccess,
                customer: {
                    ...state.customer,
                    pets:
                        existingPetIndex >= 0
                            ? state.customer.pets.map((pet, index) =>
                                  index === existingPetIndex ? action.payload.pet : pet
                              )
                            : [...state.customer.pets, action.payload.pet]
                }
            };
        }
        case ONLINE_BOOKING_SET_SELECTED_PETS:
            return {
                ...state,
                selectedPets: action.payload.pets
            };
        /*
         * Booking items
         */
        case ONLINE_BOOKING_SET_BOOKING_ITEMS:
            return {
                ...state,
                bookingItems: action.payload.items
            };
        case ONLINE_BOOKING_UPDATE_BOOKING_ITEM:
            return {
                ...state,
                bookingItems: state.bookingItems.map(item =>
                    item.uuid === action.payload.uuid
                        ? ({ ...item, ...action.payload.options } as OnlineBookingItem)
                        : item
                )
            };
        case ONLINE_BOOKING_UPDATE_BOOKING_ITEMS: {
            const areasWithChangedSelection = SESSION_BUSINESS_AREAS.filter(businessArea =>
                action.payload.items.some(item => {
                    if (!isOnlineBookingSessionItemOf(businessArea)(item)) {
                        return false;
                    }

                    const existing = state.bookingItems.find(current => current.uuid === item.uuid);

                    return walkingUpdateClearsBookingError(
                        existing && isOnlineBookingSessionItemOf(businessArea)(existing)
                            ? existing
                            : undefined,
                        item
                    );
                })
            );

            return areasWithChangedSelection.reduce(
                (nextState, businessArea) =>
                    updateSessionCache(nextState, businessArea, cache => ({
                        ...cache,
                        lastBookingError: undefined,
                        lastBookingErrorItemUuid: undefined
                    })),
                {
                    ...state,
                    bookingItems: state.bookingItems.map(item => {
                        const newItem = action.payload.items.find(i => i.uuid === item.uuid);

                        if (newItem) {
                            return { ...item, ...newItem } as OnlineBookingItem;
                        }

                        return item;
                    })
                }
            );
        }
        case ONLINE_BOOKING_REMOVE_BOOKING_ITEMS:
            return {
                ...state,
                bookingItems: state.bookingItems.filter(
                    item => !action.payload.uuids.includes(item.uuid)
                )
            };
        /*
         * Service category
         */
        case ONLINE_BOOKING_SET_SELECTED_CATEGORY:
            return {
                ...state,
                currentCategory: action.payload.category
            };
        case ONLINE_BOOKING_SET_EVALUATION_SERVICE_CATEGORY:
            return {
                ...state,
                evaluationServiceCategory: action.payload.category
            };
        /*
         * Selected staff
         */
        case ONLINE_BOOKING_SET_SELECTED_STAFF:
            return {
                ...state,
                selectedStaff: action.payload.staff
            };
        /*
         * Availability
         */
        case ONLINE_BOOKING_GET_GROOMING_STAFF_NEXT_AVAILABLE_DATE_START:
            return {
                ...state,
                isPending: true,
                status: OnlineBookingStatus.GetGroomingStaffNextAvailableDateStart
            };
        case ONLINE_BOOKING_GET_GROOMING_STAFF_NEXT_AVAILABLE_DATE_SUCCESS:
            return {
                ...state,
                isPending: false,
                status: OnlineBookingStatus.GetGroomingStaffNextAvailableDateSuccess
            };
        case ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_START:
            return {
                ...state,
                isPending: true,
                status: OnlineBookingStatus.GetGroomingStaffAvailabilityStart
            };
        case ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_SUCCESS:
            return {
                ...state,
                isPending: false,
                status: OnlineBookingStatus.GetGroomingStaffAvailabilitySuccess
            };
        case ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_START: {
            const { dateKey, requestKey } = action.payload;
            const { [dateKey]: _removed, ...remainingAvailability } =
                state.daycareAvailabilityByDate;

            return {
                ...state,
                daycareAvailabilityByDate: remainingAvailability,
                daycareAvailabilityLoadingByDate: {
                    ...state.daycareAvailabilityLoadingByDate,
                    [dateKey]: true
                },
                daycareAvailabilityRequestKeyByDate: {
                    ...state.daycareAvailabilityRequestKeyByDate,
                    [dateKey]: requestKey
                }
            };
        }
        case ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_SUCCESS: {
            const { dateKey, requestKey, availability } = action.payload;

            if (state.daycareAvailabilityRequestKeyByDate[dateKey] !== requestKey) {
                return state;
            }

            return {
                ...state,
                daycareAvailabilityByDate: {
                    ...state.daycareAvailabilityByDate,
                    [dateKey]: availability
                },
                daycareAvailabilityLoadingByDate: {
                    ...state.daycareAvailabilityLoadingByDate,
                    [dateKey]: false
                }
            };
        }
        case ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_FAILED: {
            const { dateKey, requestKey } = action.payload;

            if (state.daycareAvailabilityRequestKeyByDate[dateKey] !== requestKey) {
                return state;
            }

            const { [dateKey]: _removed, ...remainingAvailability } =
                state.daycareAvailabilityByDate;

            return {
                ...state,
                daycareAvailabilityByDate: remainingAvailability,
                daycareAvailabilityLoadingByDate: {
                    ...state.daycareAvailabilityLoadingByDate,
                    [dateKey]: false
                }
            };
        }
        case ONLINE_BOOKING_CLEAR_DAYCARE_AVAILABILITY:
            return {
                ...state,
                daycareAvailabilityByDate: {},
                daycareAvailabilityLoadingByDate: {},
                daycareAvailabilityRequestKeyByDate: {}
            };
        case ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_START:
            return {
                ...state,
                boardingAvailability: undefined,
                boardingAvailabilityLoading: true,
                boardingAvailabilityRequestKey: action.payload.requestKey
            };
        case ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_SUCCESS:
            if (state.boardingAvailabilityRequestKey !== action.payload.requestKey) {
                return state;
            }

            return {
                ...state,
                boardingAvailability: action.payload.availability,
                boardingAvailabilityLoading: false
            };
        case ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_FAILED:
            if (state.boardingAvailabilityRequestKey !== action.payload.requestKey) {
                return state;
            }

            return {
                ...state,
                boardingAvailability: undefined,
                boardingAvailabilityLoading: false
            };
        case ONLINE_BOOKING_CLEAR_BOARDING_AVAILABILITY:
            return {
                ...state,
                boardingAvailability: undefined,
                boardingAvailabilityLoading: false,
                boardingAvailabilityRequestKey: undefined
            };
        case ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_START:
            return updateSessionCache(state, action.payload.businessArea, cache => ({
                ...cache,
                eligibility: startRequest(cache.eligibility, action.payload.requestKey)
            }));
        case ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_SUCCESS: {
            const { businessArea, requestKey, data } = action.payload;
            const eligibleIds = new Set(data.services.map(service => service.id));
            const isAreaItem = isOnlineBookingSessionItemOf(businessArea);

            return {
                ...updateSessionCache(state, businessArea, cache => ({
                    ...cache,
                    eligibility: {
                        ...cache.eligibility,
                        [requestKey]: {
                            loading: false,
                            data
                        }
                    }
                })),
                bookingItems: state.bookingItems.map(item => {
                    if (!isAreaItem(item) || !item.service) {
                        return item;
                    }

                    if (eligibleIds.has(item.service.id)) {
                        return item;
                    }

                    return {
                        ...clearWalkingItemDateTime(clearWalkingItemStaffSelection(item)),
                        service: undefined
                    };
                })
            };
        }
        case ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_FAILED:
            return updateSessionCache(state, action.payload.businessArea, cache => ({
                ...cache,
                eligibility: failRequest(
                    cache.eligibility,
                    action.payload.requestKey,
                    action.payload.error
                )
            }));
        case ONLINE_BOOKING_CLEAR_WALKING_ELIGIBILITY:
            return SESSION_BUSINESS_AREAS.reduce(
                (nextState, businessArea) =>
                    updateSessionCache(nextState, businessArea, cache => ({
                        ...cache,
                        eligibility: {}
                    })),
                state
            );
        case ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_START:
            return updateSessionCache(state, action.payload.businessArea, cache => ({
                ...cache,
                staffAvailability: startRequest(cache.staffAvailability, action.payload.requestKey)
            }));
        case ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_SUCCESS:
            return updateSessionCache(state, action.payload.businessArea, cache => ({
                ...cache,
                staffAvailability: {
                    ...cache.staffAvailability,
                    [action.payload.requestKey]: {
                        loading: false,
                        data: action.payload.data
                    }
                }
            }));
        case ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_FAILED:
            return updateSessionCache(state, action.payload.businessArea, cache => ({
                ...cache,
                staffAvailability: failRequest(
                    cache.staffAvailability,
                    action.payload.requestKey,
                    action.payload.error
                )
            }));
        case ONLINE_BOOKING_GET_WALKING_AVAILABILITY_START:
            return updateSessionCache(state, action.payload.businessArea, cache => ({
                ...cache,
                availability: startRequest(cache.availability, action.payload.requestKey)
            }));
        case ONLINE_BOOKING_GET_WALKING_AVAILABILITY_SUCCESS:
            return updateSessionCache(state, action.payload.businessArea, cache => ({
                ...cache,
                availability: {
                    ...cache.availability,
                    [action.payload.requestKey]: {
                        loading: false,
                        data: action.payload.data
                    }
                }
            }));
        case ONLINE_BOOKING_GET_WALKING_AVAILABILITY_FAILED:
            return updateSessionCache(state, action.payload.businessArea, cache => ({
                ...cache,
                availability: failRequest(
                    cache.availability,
                    action.payload.requestKey,
                    action.payload.error
                )
            }));
        case ONLINE_BOOKING_RECOVER_WALKING_BOOKING_ERROR: {
            const { businessArea } = action.payload;
            const isAreaItem = isOnlineBookingSessionItemOf(businessArea);

            return {
                ...updateSessionCache(state, businessArea, cache => {
                    const availability = { ...cache.availability };
                    const staffAvailability = { ...cache.staffAvailability };

                    action.payload.availabilityKeysToRemove.forEach(key => {
                        delete availability[key];
                    });
                    action.payload.staffAvailabilityKeysToRemove.forEach(key => {
                        delete staffAvailability[key];
                    });

                    return {
                        ...cache,
                        lastBookingError: action.payload.code,
                        lastBookingErrorItemUuid: action.payload.itemUuid,
                        availability,
                        staffAvailability
                    };
                }),
                bookingItems: state.bookingItems.map(item =>
                    isAreaItem(item) ? clearWalkingItemTime(item) : item
                )
            };
        }
        /*
         * Agreements
         */
        case ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE:
            return {
                ...state,
                agreementSignatures: [
                    ...state.agreementSignatures.filter(
                        signature => signature.agreementId !== action.payload.signature.agreementId
                    ),
                    action.payload.signature
                ]
            };
        case ONLINE_BOOKING_AGREEMENT_SIGNATURE_PERSISTED:
            return {
                ...state,
                agreementSignatures: state.agreementSignatures.map(signature =>
                    signature.agreementId === action.payload.agreementId
                        ? { ...signature, persisted: true }
                        : signature
                )
            };
        case ONLINE_BOOKING_AGREEMENT_SIGNATURE_UPDATED:
            return {
                ...state,
                agreementSignatures: state.agreementSignatures.map(signature =>
                    signature.agreementId === action.payload.agreementId
                        ? { ...signature, ...action.payload.changes }
                        : signature
                )
            };
        /*
         * Notes
         */
        case ONLINE_BOOKING_SET_NOTES:
            return {
                ...state,
                notes: action.payload.notes
            };
        /*
         * Bookings
         */
        case ONLINE_BOOKING_CREATE_BOOKING_START:
            return {
                ...state,
                isPending: true,
                status: OnlineBookingStatus.CreateBookingStart
            };
        case ONLINE_BOOKING_CREATE_BOOKING_SUCCESS:
            return {
                ...state,
                isPending: false,
                status: OnlineBookingStatus.CreateBookingSuccess
            };
        case ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_START:
            return {
                ...state,
                isPending: true,
                status: OnlineBookingStatus.ConfirmBookingDepositStart
            };
        case ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_SUCCESS:
            return {
                ...state,
                isPending: false,
                status: OnlineBookingStatus.ConfirmBookingDepositSuccess
            };
        /*
         * Waitlist
         */
        case ONLINE_BOOKING_SET_WAITLIST_CATEGORY:
            return {
                ...state,
                waitlistCategory: action.payload.category
            };
        case ONLINE_BOOKING_CREATE_WAITLIST_START:
            return {
                ...state,
                isPending: true,
                status: OnlineBookingStatus.CreateWaitlistStart
            };
        case ONLINE_BOOKING_CREATE_WAITLIST_SUCCESS:
            return {
                ...state,
                isPending: false,
                status: OnlineBookingStatus.CreateWaitlistSuccess
            };
        default:
            return state;
    }
};
